<template>
  <Portal>
    <ModalComponent :show="true" @close="$emit('close')">
      <Card :title="$t('settings')" class="shop-move-settings">
        <template #header>
          <ProductCard :value="value.product" theme="list" />
        </template>
        <template #default>
          <div class="shop-move-settings__fields" v-auto-animate>
            <InputComponent :label="$tc('category', 1)" required>
              <SelectComponent v-model="data.to.category">
                <option v-for="category in categoryData" :value="category.id" :disabled="category.disabled">
                  {{ category.label }}
                </option>
              </SelectComponent>
            </InputComponent>
            <InputComponent v-if="data.to.category && subCategoryData.length" :label="$tc('subcategory', 1)" required>
              <SelectComponent v-model="data.to.subcategory">
                <option v-for="category in subCategoryData" :value="category.id">
                  {{ category.label }}
                </option>
              </SelectComponent>
            </InputComponent>
            <Alert v-if="isDuplicate" theme="warning">
              {{ $t('productMoveDuplicate') }}
            </Alert>
            <CheckboxComponent v-model="data.isDefault" :label="$t('setStandard')" />
            <CheckboxComponent v-model="data.isSelected" :label="$t('setSelected')" />
          </div>
        </template>
        <template #footer>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="$emit('close')" />
          <ButtonComponent
            :label="$t('confirm')"
            :icon="['fal', 'angle-right']"
            theme="primary"
            @click="submit"
            :disabled="!validator.passes || isDuplicate"
          />
        </template>
      </Card>
    </ModalComponent>
  </Portal>
</template>

<script>
import Card from '@/components/Card'
import { AlertComponent as Alert } from 'vue-elder-alert'

import { ModalComponent } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import Portal from '@/components/Portal'
import ProductCard from '@/components/Product/Card'
import SelectComponent from '@/components/Select/Select'
import Validator from '@/mixins/validator-v2'

export default {
  mixins: [
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'data.to',
    }),
  ],
  props: {
    value: Object,
    categories: Array,
  },
  watch: {
    'data.to.category'(prev, next) {
      if (prev && prev !== next) this.data.to.subcategory = null
    },
  },
  data() {
    return {
      data: {
        isDefault: false,
        isSelected: false,
        to: {
          category: null,
          subcategory: null,
        },
      },
    }
  },
  computed: {
    labels() {
      return {
        category: this.$tc('category', 1),
        subcategory: this.$tc('subcategory', 1),
      }
    },
    rules() {
      return {
        category: 'required',
        subcategory: 'required',
      }
    },
    isDuplicate() {
      return this.categoryData.some(category => {
        return category.categories.some(subcategory => {
          return (
            subcategory.disabled &&
            subcategory.id === this.data.to.subcategory &&
            this.from[0].subcategory !== subcategory.id
          )
        })
      })
    },
    categoryData() {
      if (!this.categories || this.categories.length === 0) return []

      return this.categories
        .map(category => {
          const subcategories = (category.categories || []).map(subcategory => {
            return {
              id: subcategory.id,
              label: subcategory.label,
              disabled: subcategory.items.some(item => item.product.id === this.value.product.id),
            }
          })

          return {
            id: category.id,
            label: category.label,
            categories: subcategories,
            disabled: subcategories.length === 0 || this.$store.getters['Shop/isCategoryLocked'](category.id),
          }
        })
        .filter(Boolean)
    },
    subCategoryData() {
      if (!this.categoryData || this.categoryData.length === 0) return []
      return (this.categoryData.find(c => c.id === this.data.to.category) || {}).categories || []
    },
    from() {
      return this.categories
        .map(category => {
          return (category.categories || []).map(subcategory => {
            const item = subcategory.items.find(item => item.id === this.value.id)

            if (!item) return
            return {
              category: category.id,
              subcategory: subcategory.id,
            }
          })
        })
        .flat(Infinity)
        .filter(Boolean)
    },
  },
  methods: {
    submit() {
      if (!this.validator.passes) return

      this.$emit('input', {
        ...this.data,
        from: this.from[0],
        product: this.value,
      })
    },
  },
  components: {
    Alert,
    Card,
    ModalComponent,
    InputComponent,
    CheckboxComponent,
    Portal,
    ProductCard,
    SelectComponent,
  },
}
</script>

<style lang="scss">
.shop-move-settings {
  width: 100%;
  max-width: 600px;

  & > .card__header {
    min-height: unset;
    display: flex;
    align-items: unset;

    .product-card .card__header {
      height: 4rem;
      width: 4rem;
    }

    img {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-md);
    }

    .product-card__subtitle {
      display: none;
    }
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .product-quantity-settings {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .alert__content {
    white-space: pre-line;
  }

  .elder-checkbox {
    &--theme-toggle {
      flex-direction: row-reverse;
      gap: 0.5rem;

      .elder-checkbox__box {
        margin-right: 0;
      }
      .elder-checkbox__label {
        flex-grow: 1;
      }
    }
  }
}
</style>
